import { z } from 'zod';
import { DEMO_SESSION_DURATION_VALUES } from '@lgc/database/types/domains/club';
import { AUDIENCE_CLASS_VALUES } from '@lgc/database/types/domains/bay';
import 'date-fns-tz';
import { PAYMENT_TYPE } from '@lgc/database/types/domains/booking';

// src/lessons/packState.ts
var LESSON_PACK_STATUS_LABEL = {
  scheduled: "Upcoming",
  active: "Active",
  expired: "Expired",
  used: "Used",
  canceled: "Canceled"
};
var LESSON_PACK_FALLBACK_NAME = "Lesson Package";
function lessonPackName(pack) {
  return pack.lesson?.name ?? LESSON_PACK_FALLBACK_NAME;
}
function isLessonPackSpendable(pack) {
  return pack.status === "active" || pack.status === "scheduled" || pack.status === "used" && pack.remainingMinutes !== null && pack.remainingMinutes > 0;
}
function lessonPackBadgeLabel(pack) {
  if (pack.status === "scheduled") return LESSON_PACK_STATUS_LABEL.scheduled;
  return isLessonPackSpendable(pack) ? LESSON_PACK_STATUS_LABEL.active : LESSON_PACK_STATUS_LABEL[pack.status];
}
function lessonPackCeilingMinutes(pack) {
  return pack.maxTotalMinutes ?? pack.lesson?.maxTotalMinutes ?? null;
}
function isLessonPackUnlimited(pack) {
  return pack.remainingMinutes == null || lessonPackCeilingMinutes(pack) == null;
}
function isLessonPackSpent(pack) {
  return !isLessonPackUnlimited(pack) && pack.remainingMinutes === 0;
}
function lessonPackUsedMinutes(pack) {
  if (isLessonPackUnlimited(pack)) return null;
  const ceiling = lessonPackCeilingMinutes(pack);
  const remaining = pack.remainingMinutes;
  return Math.min(Math.max(ceiling - remaining, 0), ceiling);
}
function isLessonPackTombstoned(pack) {
  return pack.lesson?.deletedAt != null;
}
function splitLessonPacks(packs) {
  const current = [];
  const history = [];
  for (const pack of packs) {
    (isLessonPackSpendable(pack) ? current : history).push(pack);
  }
  return { current, history };
}

// src/datetime/errors.ts
var DateTimeLibraryError = class extends Error {
  constructor(type, message, details) {
    super(message);
    this.name = "DateTimeLibraryError";
    this.type = type;
    this.details = details;
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, this.constructor);
    }
  }
};
var InvalidDateError = class extends DateTimeLibraryError {
  constructor(message) {
    super("INVALID_DATE", message);
    this.name = "InvalidDateError";
  }
};

// src/datetime/timeString.ts
var END_OF_DAY = "24:00";
function isEndOfDaySentinel(timeString) {
  return timeString === END_OF_DAY || timeString === "24:00:00";
}
var MAX_OVERFLOW_HOUR = 47;
var TIME_PATTERN = /^(\d{1,2}):(\d{1,2})(?::(\d{1,2}))?$/;
function parseTimeOfDay(timeString, options = {}) {
  if (!timeString || typeof timeString !== "string") {
    throw new InvalidDateError("Time string must be a non-empty string");
  }
  const match = TIME_PATTERN.exec(timeString);
  if (!match) {
    throw new InvalidDateError(
      `Invalid time format: ${timeString}. Expected HH:mm or HH:mm:ss`
    );
  }
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  const seconds = match[3] === void 0 ? 0 : Number(match[3]);
  if (minutes > 59) {
    throw new InvalidDateError(`Invalid minute: ${minutes}. Expected 0-59`);
  }
  if (seconds > 59) {
    throw new InvalidDateError(`Invalid second: ${seconds}. Expected 0-59`);
  }
  if (hours > 23) {
    if (options.allowDayOverflow) {
      if (hours > MAX_OVERFLOW_HOUR) {
        throw new InvalidDateError(
          `Invalid hour: ${hours}. Expected 0-${MAX_OVERFLOW_HOUR} when day overflow is allowed`
        );
      }
      return { hours, minutes, seconds };
    }
    if (options.allowEndOfDay && hours === 24 && minutes === 0 && seconds === 0) {
      return { hours, minutes, seconds };
    }
    if (hours === 24 && minutes === 0 && seconds === 0) {
      throw new InvalidDateError(
        `Hour 24 is the end-of-day sentinel, not a wall-clock time. Use createClubDateTimeFromSlot() for slot times that may be "${END_OF_DAY}".`
      );
    }
    throw new InvalidDateError(
      `Invalid hour: ${hours}. Expected 0-23${options.allowEndOfDay ? ` (or ${END_OF_DAY} for end-of-day)` : ""}`
    );
  }
  return { hours, minutes, seconds };
}

// src/shared/hours.ts
function roundHours(minutes, decimals = 1) {
  const scale = 10 ** decimals;
  return Math.round(minutes * scale / 60) / scale;
}

// src/lessons/formatLessonHours.ts
function lessonMinutesToHours(minutes) {
  return roundHours(minutes, 1);
}
function formatLessonHours(minutes) {
  if (minutes == null) return "Unlimited";
  const hours = lessonMinutesToHours(minutes);
  return `${hours} ${hours === 1 ? "Hour" : "Hours"}`;
}
function formatLessonHoursSentence(minutes) {
  if (minutes == null) return "Unlimited lesson hours";
  const hours = lessonMinutesToHours(minutes);
  return `${hours} lesson ${hours === 1 ? "hour" : "hours"}`;
}
function formatLessonHoursValue(minutes) {
  return String(lessonMinutesToHours(minutes));
}
var TimeSegmentSchema = z.object({
  startMinutes: z.number().int("Start minutes must be an integer").min(0, "Start minutes cannot be negative").max(1439, "Start minutes cannot exceed 1439 (11:59 PM)"),
  endMinutes: z.number().int("End minutes must be an integer").min(0, "End minutes cannot be negative").max(1560, "End minutes cannot exceed 1560 (2:00 AM next day)")
}).refine(
  (data) => data.startMinutes < data.endMinutes,
  { message: "Start time must be before end time" }
).refine(
  // An open window is owned by the day it opens and may spill at most into the
  // next day, so it can never be longer than 24h. endMinutes === startMinutes+1440
  // is a window that closes exactly 24h later (e.g. {0,1440} = the 24/7 day).
  (data) => data.endMinutes <= data.startMinutes + 1440,
  { message: "A window cannot be longer than 24 hours" }
);
var DayScheduleSchema = z.array(TimeSegmentSchema).refine(
  // Segments within a single day must not overlap. Sorted by start, each segment
  // must begin at or after the previous one ends (spillover endMinutes > 1440 is
  // accounted for since the comparison uses raw endMinutes).
  (segments) => {
    const sorted = [...segments].sort((a, b) => a.startMinutes - b.startMinutes);
    for (let i = 1; i < sorted.length; i++) {
      if (sorted[i].startMinutes < sorted[i - 1].endMinutes) return false;
    }
    return true;
  },
  { message: "Schedule segments must not overlap" }
);
var WeeklyScheduleSchema = z.object({
  monday: DayScheduleSchema,
  tuesday: DayScheduleSchema,
  wednesday: DayScheduleSchema,
  thursday: DayScheduleSchema,
  friday: DayScheduleSchema,
  saturday: DayScheduleSchema,
  sunday: DayScheduleSchema
});
var DateStringSchema = z.string().regex(
  /^\d{4}-\d{2}-\d{2}$/,
  "Date must be in YYYY-MM-DD format"
);
var ScheduleExceptionKindSchema = z.enum(["hours", "closed"]);
var ScheduleExceptionSchema = z.object({
  date: DateStringSchema,
  kind: ScheduleExceptionKindSchema,
  schedule: DayScheduleSchema,
  reason: z.string().optional(),
  isPublic: z.boolean().optional()
}).refine(
  (data) => data.schedule.length > 0,
  { message: 'An exception must declare at least one segment \u2014 closed all day is kind "closed" with 00:00\u201324:00' }
);
z.record(
  DateStringSchema,
  ScheduleExceptionSchema
);
var MAX_DEMO_ADVANCE_MINUTES = 30 * 24 * 60;
var MAX_LESSON_DURATION_MINUTES = 120;
var DemoBookingConfigSchema = z.object({
  minAdvanceMinutes: z.number().int("Minimum advance notice must be a whole number of minutes").min(0, "Minimum advance notice cannot be negative").max(MAX_DEMO_ADVANCE_MINUTES, "Minimum advance notice cannot exceed 30 days"),
  // Driven by the values array, not by indexing its slots — adding a third
  // allowed duration widens this schema with the TS type and the settings
  // radio in one edit, instead of silently rejecting the new option.
  sessionDurationMinutes: z.custom(
    (n) => typeof n === "number" && DEMO_SESSION_DURATION_VALUES.includes(n),
    { message: `Session duration must be one of: ${DEMO_SESSION_DURATION_VALUES.join(", ")}` }
  ),
  hours: WeeklyScheduleSchema
});
z.object({
  enabled: z.boolean().optional(),
  config: DemoBookingConfigSchema.optional()
}).refine(
  (body) => body.enabled !== void 0 || body.config !== void 0,
  { message: "At least one field is required: enabled or config" }
);
var AudienceEntrySchema = z.union([
  z.object({ kind: z.enum(AUDIENCE_CLASS_VALUES) }).strict(),
  z.object({ kind: z.literal("plan"), planType: z.literal("membership"), id: z.number().int().positive() }).strict()
]);
var BayAccessSegmentSchema = z.object({
  startMinutes: z.number().int().min(0).max(1439),
  endMinutes: z.number().int().min(0).max(1560),
  audience: z.array(AudienceEntrySchema).min(1, "Choose who may book in this window")
}).strict().refine((s) => s.startMinutes < s.endMinutes, { message: "Start time must be before end time" }).refine((s) => s.endMinutes <= s.startMinutes + 1440, { message: "A window cannot be longer than 24 hours" }).refine(
  (s) => new Set(s.audience.map((a) => a.kind === "plan" ? `plan:${a.id}` : a.kind)).size === s.audience.length,
  { message: "An audience lists each entry once" }
);
var BayAccessDaySchema = z.array(BayAccessSegmentSchema).min(1, "A customized day needs at least one window").refine(
  (segments) => {
    const sorted = [...segments].sort((a, b) => a.startMinutes - b.startMinutes);
    for (let i = 1; i < sorted.length; i++) {
      if (sorted[i].startMinutes < sorted[i - 1].endMinutes) return false;
    }
    return true;
  },
  { message: "Windows on one day must not overlap" }
);
z.object({
  monday: BayAccessDaySchema.optional(),
  tuesday: BayAccessDaySchema.optional(),
  wednesday: BayAccessDaySchema.optional(),
  thursday: BayAccessDaySchema.optional(),
  friday: BayAccessDaySchema.optional(),
  saturday: BayAccessDaySchema.optional(),
  sunday: BayAccessDaySchema.optional()
}).strict();

// src/lessons/coverage.ts
function resolveActivePack(packs) {
  const eligible = packs.filter(
    (pack) => pack.status === "active" || pack.status === "used" && pack.remainingMinutes !== null && pack.remainingMinutes > 0
  );
  if (eligible.length === 0) return null;
  const rank = (pack) => pack.status === "active" ? 0 : 1;
  return eligible.reduce((best, candidate) => {
    const bestRank = rank(best);
    const candidateRank = rank(candidate);
    if (candidateRank !== bestRank) return candidateRank < bestRank ? candidate : best;
    const bestTime = new Date(best.purchaseDate).getTime();
    const candidateTime = new Date(candidate.purchaseDate).getTime();
    if (candidateTime !== bestTime) return candidateTime < bestTime ? candidate : best;
    return candidate.id < best.id ? candidate : best;
  });
}
function resolveLessonCoverage(pack, durationMinutes) {
  if (pack === null) return { kind: "none" };
  const packIsUnlimited = pack.remainingMinutes === null;
  const coveredMinutes = packIsUnlimited ? durationMinutes : Math.min(pack.remainingMinutes ?? 0, durationMinutes);
  const chargeableMinutes = durationMinutes - coveredMinutes;
  if (chargeableMinutes > 0) {
    return { kind: "partial", pack, coveredMinutes, chargeableMinutes };
  }
  return { kind: "full", pack, unlimited: packIsUnlimited, minutesUsed: coveredMinutes };
}
function lessonRequiresPaymentMethod(coverage) {
  return coverage.kind !== "full";
}
function isOverLessonDurationLimit(minutes) {
  return minutes > MAX_LESSON_DURATION_MINUTES;
}
var LESSON_DURATION_LIMIT_MESSAGE = `Lessons can be up to ${MAX_LESSON_DURATION_MINUTES} minutes long. Please choose a shorter time.`;
var LESSON_COVERAGE_ROW_LABEL = "Lesson Package";
var LESSON_COVERAGE_STATE = {
  full: "Fully covered",
  covered: "Covered",
  partial: "Partly covered"
};
var LESSON_OVERAGE_LABEL = "Overage";
function lessonCoverageSentence(kind) {
  return kind === "full" ? "Your Lesson Package covers this lesson." : "Your Lesson Package covers part of this lesson.";
}
function lessonHoursUsedSentence(minutes) {
  return `${formatLessonHoursSentence(minutes)} will be used.`;
}
function lessonHoursReturnedSentence(minutes) {
  return `${formatLessonHoursSentence(minutes)} returned to your Lesson Package.`;
}
function lessonPartialCoverageSentence(coveredMinutes) {
  return `Your Lesson Package covers ${formatLessonHoursSentence(coveredMinutes)} of this lesson. The rest is charged at the instructor's hourly rate.`;
}
function lessonCancelOutcome(restore, credit) {
  if (restore === "unlimited") {
    return credit ? `Your account will be credited ${credit}. Your Lesson Package is unlimited \u2014 nothing to return.` : "Your Lesson Package is unlimited \u2014 nothing to return.";
  }
  const hours = formatLessonHoursSentence(restore.minutes);
  return credit ? `Your account will be credited ${credit} and ${hours} will be returned to your Lesson Package.` : `${hours} will be returned to your Lesson Package.`;
}
function lessonCancelledMessage(restore, credited) {
  if (restore === "unlimited") {
    return credited ? `${credited} has been credited to your account. Your Lesson Package is unlimited \u2014 nothing was returned.` : "Your Lesson Package is unlimited \u2014 nothing was returned.";
  }
  if (restore.minutes <= 0) {
    return credited ? `${credited} has been credited to your account.` : "Your lesson has been cancelled.";
  }
  const hours = formatLessonHoursSentence(restore.minutes);
  const verb = lessonMinutesToHours(restore.minutes) === 1 ? "has" : "have";
  const returned = `${hours} ${verb} been returned to your Lesson Package.`;
  return credited ? `${credited} has been credited to your account and ${returned}` : returned;
}

// src/lessons/instructorDisplay.ts
function lessonInstructorName(ref) {
  return `${ref.firstName} ${ref.lastName}`;
}
function resolveInstructorDisplay(params) {
  const { instructorId, activePack, clubInstructors, sessionName, sessionImage } = params;
  if (instructorId === null) return null;
  const fromPack = activePack?.allowedInstructors.find((instructor) => instructor.id === instructorId);
  if (fromPack) {
    return { name: lessonInstructorName(fromPack), image: fromPack.image };
  }
  const fromClub = clubInstructors.find((instructor) => instructor.id === instructorId);
  if (fromClub) {
    return { name: lessonInstructorName(fromClub), image: fromClub.image };
  }
  if (sessionName) {
    return { name: sessionName, image: sessionImage ?? null };
  }
  return null;
}
function timeStringToMinutes(timeString) {
  if (isEndOfDaySentinel(timeString)) {
    return 1440;
  }
  const { hours, minutes } = parseTimeOfDay(timeString);
  return hours * 60 + minutes;
}

// src/lessons/availability.ts
var LESSON_GRID_INTERVAL_MINUTES = 30;
function lessonRangeMinutes(startTime, endTime) {
  return timeStringToMinutes(endTime) - timeStringToMinutes(startTime);
}
function findLessonSlot(slots, startMinutes, durationMinutes) {
  return slots.find((slot) => slot.startMinutes === startMinutes && slot.durationMinutes === durationMinutes);
}
function lessonBaysForSlot(availability, startMinutes, durationMinutes) {
  const slot = findLessonSlot(availability.slots, startMinutes, durationMinutes);
  if (!slot || !slot.isAvailable || slot.canStart === false) return [];
  const free = new Set(slot.startBayIds ?? slot.availableBayIds ?? []);
  return availability.bays.filter((bay) => bay.bookable && free.has(bay.id));
}
var LESSON_PLANS_ACTION_LABEL = "View My Plans";
function lessonRestore(booking) {
  if (booking.userLessonId == null) return null;
  return booking.entitlementMinutesConsumed == null ? "unlimited" : { minutes: booking.entitlementMinutesConsumed };
}
function lessonRestoreAfterCancel(booking, response) {
  const predicted = lessonRestore(booking);
  if (predicted === null || predicted === "unlimited") return predicted;
  return response.lessonMinutesRestored !== void 0 ? { minutes: response.lessonMinutesRestored } : predicted;
}
var dropCoverageState = (rows) => rows.filter((r) => r.label !== LESSON_COVERAGE_ROW_LABEL);
function lessonCoverage(booking, charged) {
  const restore = lessonRestore(booking);
  const minutes = restore && restore !== "unlimited" ? restore.minutes : null;
  const cancelled = booking.status === "cancelled";
  const returned = minutes !== null ? lessonHoursReturnedSentence(minutes) : null;
  const note = (kind) => minutes !== null ? `${lessonCoverageSentence(kind)} ${lessonHoursUsedSentence(minutes)}` : lessonCoverageSentence(kind);
  switch (booking.paymentType) {
    case PAYMENT_TYPE.LESSON: {
      const rows = charged ? [
        { label: LESSON_COVERAGE_ROW_LABEL, value: LESSON_COVERAGE_STATE.covered },
        { label: "Additional charges", value: charged }
      ] : [{ label: LESSON_COVERAGE_ROW_LABEL, value: LESSON_COVERAGE_STATE.full }];
      if (cancelled) return { rows: dropCoverageState(rows), note: returned };
      if (restore === null) return { rows, note: null };
      return { rows, note: note("full") };
    }
    case PAYMENT_TYPE.LESSON_HYBRID: {
      const rows = [
        { label: LESSON_COVERAGE_ROW_LABEL, value: LESSON_COVERAGE_STATE.partial },
        { label: LESSON_OVERAGE_LABEL, value: charged ?? "$0.00" }
      ];
      if (cancelled) return { rows: dropCoverageState(rows), note: returned };
      return { rows, note: note("partial") };
    }
    default:
      return null;
  }
}
function lessonRatePerHour() {
  return null;
}

export { LESSON_COVERAGE_ROW_LABEL, LESSON_COVERAGE_STATE, LESSON_DURATION_LIMIT_MESSAGE, LESSON_GRID_INTERVAL_MINUTES, LESSON_OVERAGE_LABEL, LESSON_PACK_FALLBACK_NAME, LESSON_PACK_STATUS_LABEL, LESSON_PLANS_ACTION_LABEL, findLessonSlot, formatLessonHours, formatLessonHoursSentence, formatLessonHoursValue, isLessonPackSpendable, isLessonPackSpent, isLessonPackTombstoned, isLessonPackUnlimited, isOverLessonDurationLimit, lessonBaysForSlot, lessonCancelOutcome, lessonCancelledMessage, lessonCoverage, lessonCoverageSentence, lessonHoursReturnedSentence, lessonHoursUsedSentence, lessonInstructorName, lessonMinutesToHours, lessonPackBadgeLabel, lessonPackCeilingMinutes, lessonPackName, lessonPackUsedMinutes, lessonPartialCoverageSentence, lessonRangeMinutes, lessonRatePerHour, lessonRequiresPaymentMethod, lessonRestore, lessonRestoreAfterCancel, resolveActivePack, resolveInstructorDisplay, resolveLessonCoverage, splitLessonPacks };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map